import apiService from './api'
import type { User, AuthTokens, LoginCredentials, RegisterData } from '@/types/user'
import type { ApiResponse } from '@/types/api'

export interface LoginResponse {
  user: User
  tokens: AuthTokens
}

export interface RegisterResponse {
  user: User
  tokens: AuthTokens
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await apiService.post<ApiResponse<LoginResponse>>('/auth/login', credentials)
    return response.data
  }

  async register(data: RegisterData): Promise<RegisterResponse> {
    const response = await apiService.post<ApiResponse<RegisterResponse>>('/auth/register', data)
    return response.data
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout')
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error)
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const response = await apiService.post<ApiResponse<AuthTokens>>('/auth/refresh', {
      refresh_token: refreshToken,
    })
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<ApiResponse<User>>('/auth/me')
    return response.data
  }

  async updateProfile(updates: Partial<User>): Promise<User> {
    const response = await apiService.patch<ApiResponse<User>>('/auth/profile', updates)
    return response.data
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiService.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    })
  }

  async requestPasswordReset(email: string): Promise<void> {
    await apiService.post('/auth/forgot-password', { email })
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiService.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    })
  }

  async verifyEmail(token: string): Promise<void> {
    await apiService.post('/auth/verify-email', { token })
  }

  async resendVerification(): Promise<void> {
    await apiService.post('/auth/resend-verification')
  }

  // OAuth methods (for future implementation)
  async loginWithGitHub(): Promise<string> {
    const response = await apiService.get<ApiResponse<{ url: string }>>('/auth/github')
    return response.data.url
  }

  async handleOAuthCallback(code: string, state: string): Promise<LoginResponse> {
    const response = await apiService.post<ApiResponse<LoginResponse>>('/auth/oauth/callback', {
      code,
      state,
    })
    return response.data
  }
}

export const authService = new AuthService()
export default authService